/**
 * Trainova AI — Tenant Language & Localization Management Component
 *
 * Implements:
 * - Enabling/disabling multiple organization languages
 * - Default tenant language configuration
 * - Default newly onboarded user language
 * - Toggle for learner display language switching
 * - Fallback language selection
 * - Interactive Google Cloud translation preview & test console
 * - Strict tenant isolation & RBAC enforcement
 */

import React, { useState, useEffect } from 'react';
import { api } from '../../../services/api';
import { SYSTEM_LANGUAGES, LanguageMeta } from '../../../i18n/types';

interface TenantLanguageConfig {
  supportedLanguages: string[];
  defaultLanguage: string;
  fallbackLanguage: string;
  allowUserLanguageSelection: boolean;
  defaultOnboardingLanguage: string;
  availableLanguages: LanguageMeta[];
}

interface TranslationTestResult {
  sourceText: string;
  targetLang: string;
  translatedText: string;
  provider: string;
  cached: boolean;
  characterCount: number;
  durationMs: number;
}

export const TenantLanguageSettingsTab: React.FC = () => {
  const [config, setConfig] = useState<TenantLanguageConfig>({
    supportedLanguages: ['en'],
    defaultLanguage: 'en',
    fallbackLanguage: 'en',
    allowUserLanguageSelection: true,
    defaultOnboardingLanguage: 'en',
    availableLanguages: SYSTEM_LANGUAGES,
  });

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Live Test Translation state
  const [testText, setTestText] = useState('Welcome to your AI simulation workspace. Speak clearly into your microphone.');
  const [testTargetLang, setTestTargetLang] = useState('es');
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<TranslationTestResult | null>(null);
  const [testError, setTestError] = useState<string | null>(null);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setIsLoading(true);
      setSaveError(null);
      const res = await api.languages.getAdminConfig();
      if (res.success && res.data) {
        setConfig({
          supportedLanguages: res.data.supportedLanguages || ['en'],
          defaultLanguage: res.data.defaultLanguage || 'en',
          fallbackLanguage: res.data.fallbackLanguage || 'en',
          allowUserLanguageSelection: res.data.allowUserLanguageSelection ?? true,
          defaultOnboardingLanguage: res.data.defaultOnboardingLanguage || 'en',
          availableLanguages: res.data.availableLanguages || SYSTEM_LANGUAGES,
        });
        if (res.data.supportedLanguages?.length > 1) {
          const second = res.data.supportedLanguages.find((l: string) => l !== 'en');
          if (second) setTestTargetLang(second);
        }
      }
    } catch (err: any) {
      setSaveError(err.message || 'Failed to load tenant language configuration.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleLanguage = (code: string) => {
    setConfig((prev) => {
      const exists = prev.supportedLanguages.includes(code);
      let updated: string[];

      if (exists) {
        // Prevent disabling the default language or having zero languages
        if (code === prev.defaultLanguage) {
          alert('Cannot disable the default language. Please select another default language first.');
          return prev;
        }
        if (prev.supportedLanguages.length <= 1) {
          alert('At least one language must remain enabled for your organization.');
          return prev;
        }
        updated = prev.supportedLanguages.filter((l) => l !== code);
      } else {
        updated = [...prev.supportedLanguages, code];
      }

      return {
        ...prev,
        supportedLanguages: updated,
      };
    });
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setSaveSuccess(null);
      setSaveError(null);

      const res = await api.languages.updateAdminConfig({
        supportedLanguages: config.supportedLanguages,
        defaultLanguage: config.defaultLanguage,
        fallbackLanguage: config.fallbackLanguage,
        allowUserLanguageSelection: config.allowUserLanguageSelection,
        defaultOnboardingLanguage: config.defaultOnboardingLanguage,
      });

      if (res.success) {
        setSaveSuccess('Organization language preferences and tenant policy saved successfully.');
        setTimeout(() => setSaveSuccess(null), 5000);
      } else {
        setSaveError(res.error?.message || 'Failed to save language settings.');
      }
    } catch (err: any) {
      setSaveError(err.message || 'An unexpected error occurred while saving.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRunTest = async () => {
    if (!testText.trim()) return;

    try {
      setIsTesting(true);
      setTestResult(null);
      setTestError(null);

      const res = await api.languages.testTranslation({
        text: testText,
        targetLang: testTargetLang,
      });

      if (res.success && res.data) {
        setTestResult(res.data);
      } else {
        setTestError(res.error?.message || 'Translation test failed.');
      }
    } catch (err: any) {
      setTestError(err.message || 'Translation request failed.');
    } finally {
      setIsTesting(false);
    }
  };

  if (isLoading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', background: 'var(--white)', borderRadius: '12px', border: '1px solid var(--line)' }}>
        <div className="dashboard-spinner" style={{ margin: '0 auto 12px' }} />
        <p style={{ color: 'var(--muted)', fontSize: '14px', fontWeight: 500 }}>
          Loading language configuration & translation engine...
        </p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Alert Banners */}
      {saveSuccess && (
        <div
          style={{
            padding: '12px 16px',
            background: 'var(--ok-soft)',
            color: 'var(--ok)',
            border: '1px solid rgba(15, 138, 95, 0.2)',
            borderRadius: '10px',
            fontSize: '14px',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <span>✓</span>
          <span>{saveSuccess}</span>
        </div>
      )}

      {saveError && (
        <div
          style={{
            padding: '12px 16px',
            background: 'var(--bad-soft)',
            color: 'var(--bad)',
            border: '1px solid rgba(194, 59, 59, 0.2)',
            borderRadius: '10px',
            fontSize: '14px',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <span>⚠️</span>
          <span>{saveError}</span>
        </div>
      )}

      {/* Card 1: Supported Languages Matrix */}
      <div
        style={{
          background: 'var(--white)',
          border: '1px solid var(--line)',
          borderRadius: '14px',
          padding: '24px',
          boxShadow: '0 1px 3px rgba(7, 29, 69, 0.04)',
        }}
      >
        <div style={{ marginBottom: '18px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: 700, color: 'var(--ink)', margin: '0 0 6px 0' }}>
            🌐 Supported Organization Languages
          </h3>
          <p style={{ fontSize: '14px', color: 'var(--muted)', margin: 0 }}>
            Select the languages your enterprise learners and staff can use across the Trainova AI portal, PWA, and simulation interfaces.
          </p>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            gap: '12px',
            marginBottom: '24px',
          }}
        >
          {SYSTEM_LANGUAGES.map((lang) => {
            const isChecked = config.supportedLanguages.includes(lang.code);
            const isDefault = config.defaultLanguage === lang.code;

            return (
              <label
                key={lang.code}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px 14px',
                  borderRadius: '10px',
                  border: isChecked ? '2px solid var(--blue)' : '1px solid var(--line)',
                  background: isChecked ? 'var(--blue-soft)' : 'var(--surface)',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  userSelect: 'none',
                }}
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => handleToggleLanguage(lang.code)}
                  style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: 'var(--blue)' }}
                />
                <span style={{ fontSize: '20px', lineHeight: 1 }}>{lang.flag}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--ink)' }}>
                    {lang.nativeName}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
                    {lang.name} {isDefault && <span style={{ color: 'var(--blue)', fontWeight: 700 }}>(Default)</span>}
                  </div>
                </div>
              </label>
            );
          })}
        </div>

        {/* Global Controls & Defaults */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: '18px',
            paddingTop: '20px',
            borderTop: '1px solid var(--line)',
          }}
        >
          {/* Default Tenant Language */}
          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: 'var(--ink)', marginBottom: '6px' }}>
              Default Organization Language
            </label>
            <select
              value={config.defaultLanguage}
              onChange={(e) => {
                const val = e.target.value;
                setConfig((prev) => ({
                  ...prev,
                  defaultLanguage: val,
                  // Ensure default language is in supported list
                  supportedLanguages: prev.supportedLanguages.includes(val)
                    ? prev.supportedLanguages
                    : [...prev.supportedLanguages, val],
                }));
              }}
              style={{
                width: '100%',
                height: '42px',
                padding: '0 12px',
                borderRadius: '8px',
                border: '1px solid var(--line)',
                background: 'var(--white)',
                color: 'var(--ink)',
                fontSize: '14px',
                fontWeight: 600,
                outline: 'none',
              }}
            >
              {config.supportedLanguages.map((code) => {
                const meta = SYSTEM_LANGUAGES.find((l) => l.code === code);
                return (
                  <option key={code} value={code}>
                    {meta?.flag} {meta?.nativeName} ({meta?.name})
                  </option>
                );
              })}
            </select>
            <p style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '4px' }}>
              Language used by default across public routes, notifications, and new sessions.
            </p>
          </div>

          {/* Default Onboarding Language */}
          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: 'var(--ink)', marginBottom: '6px' }}>
              Default Onboarding Language
            </label>
            <select
              value={config.defaultOnboardingLanguage}
              onChange={(e) => setConfig((prev) => ({ ...prev, defaultOnboardingLanguage: e.target.value }))}
              style={{
                width: '100%',
                height: '42px',
                padding: '0 12px',
                borderRadius: '8px',
                border: '1px solid var(--line)',
                background: 'var(--white)',
                color: 'var(--ink)',
                fontSize: '14px',
                fontWeight: 600,
                outline: 'none',
              }}
            >
              {config.supportedLanguages.map((code) => {
                const meta = SYSTEM_LANGUAGES.find((l) => l.code === code);
                return (
                  <option key={code} value={code}>
                    {meta?.flag} {meta?.nativeName} ({meta?.name})
                  </option>
                );
              })}
            </select>
            <p style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '4px' }}>
              Assigned automatically to newly invited learners during batch or SSO provisioning.
            </p>
          </div>

          {/* Fallback Language */}
          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: 'var(--ink)', marginBottom: '6px' }}>
              System Fallback Language
            </label>
            <select
              value={config.fallbackLanguage}
              onChange={(e) => setConfig((prev) => ({ ...prev, fallbackLanguage: e.target.value }))}
              style={{
                width: '100%',
                height: '42px',
                padding: '0 12px',
                borderRadius: '8px',
                border: '1px solid var(--line)',
                background: 'var(--white)',
                color: 'var(--ink)',
                fontSize: '14px',
                fontWeight: 600,
                outline: 'none',
              }}
            >
              {SYSTEM_LANGUAGES.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.flag} {lang.nativeName} ({lang.name})
                </option>
              ))}
            </select>
            <p style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '4px' }}>
              Used when a specific string has not yet been translated for the active target locale.
            </p>
          </div>
        </div>

        {/* User Switching Permission Toggle */}
        <div
          style={{
            marginTop: '20px',
            padding: '16px',
            background: 'var(--surface)',
            borderRadius: '10px',
            border: '1px solid var(--line)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '16px',
          }}
        >
          <div>
            <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--ink)' }}>
              Allow learners & staff to switch display language
            </div>
            <div style={{ fontSize: '13px', color: 'var(--muted)', marginTop: '2px' }}>
              When enabled, a language switcher appears in the navigation bar and user profile drawer. When disabled, all users strictly follow the organization default.
            </div>
          </div>
          <label style={{ position: 'relative', display: 'inline-block', width: '48px', height: '26px', flexShrink: 0 }}>
            <input
              type="checkbox"
              checked={config.allowUserLanguageSelection}
              onChange={(e) => setConfig((prev) => ({ ...prev, allowUserLanguageSelection: e.target.checked }))}
              style={{ opacity: 0, width: 0, height: 0 }}
            />
            <span
              style={{
                position: 'absolute',
                cursor: 'pointer',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: config.allowUserLanguageSelection ? 'var(--blue)' : '#CBD5E1',
                borderRadius: '26px',
                transition: '0.3s',
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  content: '""',
                  height: '20px',
                  width: '20px',
                  left: config.allowUserLanguageSelection ? '24px' : '3px',
                  bottom: '3px',
                  backgroundColor: 'white',
                  borderRadius: '50%',
                  transition: '0.3s',
                }}
              />
            </span>
          </label>
        </div>

        <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            style={{
              padding: '10px 24px',
              borderRadius: '8px',
              background: 'var(--blue)',
              color: 'var(--white)',
              border: 'none',
              fontSize: '14px',
              fontWeight: 700,
              cursor: isSaving ? 'not-allowed' : 'pointer',
              boxShadow: '0 2px 4px rgba(13, 79, 189, 0.2)',
            }}
          >
            {isSaving ? 'Saving Preferences...' : 'Save Language Settings'}
          </button>
        </div>
      </div>

      {/* Card 2: Interactive Translation Provider Test & Live Preview */}
      <div
        style={{
          background: 'var(--white)',
          border: '1px solid var(--line)',
          borderRadius: '14px',
          padding: '24px',
          boxShadow: '0 1px 3px rgba(7, 29, 69, 0.04)',
        }}
      >
        <div style={{ marginBottom: '18px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
            <h3 style={{ fontSize: '18px', fontWeight: 700, color: 'var(--ink)', margin: 0 }}>
              🧪 Google Cloud Translation Preview & Provider Test
            </h3>
            <span
              style={{
                fontSize: '12px',
                fontWeight: 700,
                padding: '4px 10px',
                borderRadius: '100px',
                background: 'var(--ok-soft)',
                color: 'var(--ok)',
                border: '1px solid rgba(15, 138, 95, 0.2)',
              }}
            >
              Google Cloud API Provider Active
            </span>
          </div>
          <p style={{ fontSize: '14px', color: 'var(--muted)', margin: '6px 0 0 0' }}>
            Test live translation of arbitrary content, verify target language rendering, and inspect cache status in real time.
          </p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: 'var(--ink)', marginBottom: '6px' }}>
              Sample Text to Translate
            </label>
            <textarea
              rows={3}
              value={testText}
              onChange={(e) => setTestText(e.target.value)}
              placeholder="Enter text to test translation..."
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '8px',
                border: '1px solid var(--line)',
                fontSize: '14px',
                fontFamily: 'inherit',
                outline: 'none',
              }}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 200px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: 'var(--ink)', marginBottom: '6px' }}>
                Target Language
              </label>
              <select
                value={testTargetLang}
                onChange={(e) => setTestTargetLang(e.target.value)}
                style={{
                  width: '100%',
                  height: '42px',
                  padding: '0 12px',
                  borderRadius: '8px',
                  border: '1px solid var(--line)',
                  background: 'var(--white)',
                  color: 'var(--ink)',
                  fontSize: '14px',
                  fontWeight: 600,
                  outline: 'none',
                }}
              >
                {SYSTEM_LANGUAGES.map((lang) => (
                  <option key={lang.code} value={lang.code}>
                    {lang.flag} {lang.nativeName} ({lang.name})
                  </option>
                ))}
              </select>
            </div>

            <div style={{ flex: '0 0 auto', paddingTop: '22px' }}>
              <button
                type="button"
                onClick={handleRunTest}
                disabled={isTesting || !testText.trim()}
                style={{
                  height: '42px',
                  padding: '0 20px',
                  borderRadius: '8px',
                  background: 'var(--navy)',
                  color: 'var(--white)',
                  border: 'none',
                  fontSize: '14px',
                  fontWeight: 700,
                  cursor: isTesting ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                {isTesting ? (
                  <>
                    <div className="dashboard-spinner" style={{ width: '16px', height: '16px' }} />
                    <span>Translating...</span>
                  </>
                ) : (
                  <>
                    <span>⚡</span>
                    <span>Test Translation</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Test Error */}
          {testError && (
            <div
              style={{
                padding: '12px 14px',
                background: 'var(--bad-soft)',
                color: 'var(--bad)',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: 600,
              }}
            >
              ⚠️ {testError}
            </div>
          )}

          {/* Test Result Display */}
          {testResult && (
            <div
              style={{
                marginTop: '10px',
                padding: '16px',
                background: 'var(--surface)',
                borderRadius: '10px',
                border: '1px solid var(--line)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', color: 'var(--muted)', letterSpacing: '0.04em' }}>
                  Translation Output ({testResult.targetLang.toUpperCase()})
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span
                    style={{
                      fontSize: '11px',
                      fontWeight: 700,
                      padding: '2px 8px',
                      borderRadius: '4px',
                      background: testResult.cached ? 'var(--blue-soft)' : 'var(--ok-soft)',
                      color: testResult.cached ? 'var(--blue)' : 'var(--ok)',
                    }}
                  >
                    {testResult.cached ? 'CACHED' : 'LIVE API'}
                  </span>
                  <span style={{ fontSize: '11px', color: 'var(--muted)' }}>
                    {testResult.durationMs}ms
                  </span>
                </div>
              </div>
              <div
                style={{
                  fontSize: '15px',
                  lineHeight: '1.6',
                  color: 'var(--ink)',
                  fontWeight: 600,
                  background: 'var(--white)',
                  padding: '12px 14px',
                  borderRadius: '8px',
                  border: '1px solid var(--line)',
                }}
              >
                {testResult.translatedText}
              </div>
              <div style={{ marginTop: '8px', fontSize: '11px', color: 'var(--muted)', display: 'flex', gap: '16px' }}>
                <span>Provider: <b>{testResult.provider}</b></span>
                <span>Characters: <b>{testResult.characterCount}</b></span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TenantLanguageSettingsTab;
